import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Sparkles, Headphones } from "lucide-react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { getSupabase } from "@/lib/supabase-lazy";
import { useActiveLang } from "@/hooks/use-active-lang";

export const Route = createFileRoute("/maya/$slug")({
  head: ({ params }) => {
    const title = `Maya — ${params.slug}`;
    return {
      meta: [
        { title },
        { name: "robots", content: "noindex" },
      ],
    };
  },
  component: MayaDestinationPage,
});

interface DestinationRow {
  id: string;
  slug: string;
  name: string;
  state: string | null;
  cover_image_url: string | null;
  status: string;
}
interface TranslationRow {
  title: string | null;
  subtitle: string | null;
  summary: string | null;
}

function MayaDestinationPage() {
  const { slug } = Route.useParams();
  const lang = useActiveLang();

  const { data, isLoading } = useQuery({
    queryKey: ["maya", "destination", slug, lang],
    queryFn: async () => {
      const { data: dest } = await (await getSupabase())
        .from("maya_destinations")
        .select("id, slug, name, state, cover_image_url, status")
        .eq("slug", slug)
        .eq("status", "published")
        .maybeSingle();
      if (!dest) return { destination: null, translation: null };
      const { data: tr } = await (await getSupabase())
        .from("maya_destination_translations")
        .select("title, subtitle, summary")
        .eq("destination_id", dest.id)
        .eq("lang", lang)
        .maybeSingle();
      return {
        destination: dest as DestinationRow,
        translation: (tr ?? null) as TranslationRow | null,
      };
    },
    staleTime: 5 * 60 * 1000,
  });

  const dest = data?.destination;
  const tr = data?.translation;

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
        <Link
          to="/maya"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Maya
        </Link>

        {isLoading ? (
          <div className="mt-10 h-72 animate-pulse rounded-2xl border border-border bg-card" />
        ) : !dest ? (
          <div className="mt-16 grid place-items-center rounded-3xl border border-dashed border-border bg-card/50 px-6 py-20 text-center">
            <div className="grid h-14 w-14 place-items-center rounded-2xl bg-primary/10 text-primary">
              <Sparkles className="h-6 w-6" />
            </div>
            <h1 className="mt-4 font-display text-2xl font-semibold">Próximamente</h1>
            <p className="mt-2 max-w-md text-muted-foreground">
              Estamos preparando esta guía narrada. Muy pronto la escucharás aquí.
            </p>
            <Link
              to="/maya"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
            >
              Ver otros destinos
            </Link>
          </div>
        ) : (
          <article className="mt-8">
            {dest.cover_image_url && (
              <div className="overflow-hidden rounded-3xl border border-border">
                <img decoding="async" fetchPriority="high"
                  src={dest.cover_image_url}
                  alt={dest.name}
                  className="aspect-[16/9] w-full object-cover"
                />
              </div>
            )}
            <div className="mt-8">
              <div className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
                {dest.state}
              </div>
              <h1 className="mt-2 font-display text-4xl font-semibold sm:text-5xl">
                {tr?.title || dest.name}
              </h1>
              {tr?.subtitle && (
                <p className="mt-3 text-lg text-muted-foreground">{tr.subtitle}</p>
              )}
              {tr?.summary && (
                <p className="mt-6 whitespace-pre-line text-base leading-relaxed text-foreground">
                  {tr.summary}
                </p>
              )}
              <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm text-muted-foreground">
                <Headphones className="h-4 w-4 text-primary" />
                Guía completa disponible con Maya Premium
              </div>
            </div>
          </article>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
